/** @jsxImportSource @emotion/react */

import { FC, useCallback, useMemo } from 'react'
import { useForm } from 'react-final-form'
import { toast } from 'utils/toast'
import * as classes from '../styles'
import { OrthodonticTableData } from './types'
import { ORTHODONTIC_TABLE_ROWS, getCellKey, MIN_TABLE_WIDTH, PATIENT_AGE_SEX_KEY, TableRowConfig } from './config'

type Props = {
  name: string
  value?: string | null
}

/*
Expected data structure (flat, only non-empty values):
{
  "patient_age_sex": "30 years old female",
  "sna_mean": "81", "sna_patient": "81",
  "snb_mean": "79", "snb_patient": "77",
  ...
}
Key format: "{rowKey}_{columnKey}" or "patient_age_sex"
Only non-empty values are stored in JSON.
*/

const parseTableData = (value: string | null | undefined): OrthodonticTableData => {
  if (!value) {
    return {}
  }

  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value
    return parsed || {}
  } catch {
    toast.error('Invalid orthodontic table data')
    console.error('Invalid orthodontic table data:', value)
    return {}
  }
}

export const OrthodonticTable: FC<Props> = ({ name, value }) => {
  const form = useForm()
  const tableData = useMemo(() => parseTableData(value), [value])

  const updateData = useCallback(
    (updater: (data: OrthodonticTableData) => OrthodonticTableData) => {
      const updated = updater(tableData)
      // Filter out empty values before saving
      const filtered: OrthodonticTableData = {}
      Object.entries(updated).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          filtered[key] = value
        }
      })
      form.change(name, Object.keys(filtered).length > 0 ? JSON.stringify(filtered) : '')
    },
    [form, name, tableData]
  )

  const updateCell = useCallback(
    (key: string, newValue: string) => {
      updateData((data) => {
        const updated = { ...data }

        if (newValue && newValue.trim() !== '') {
          updated[key] = newValue
        } else {
          // Remove key if value is empty
          delete updated[key]
        }

        return updated
      })
    },
    [updateData]
  )

  const renderRow = (config: TableRowConfig) => {
    const rowCells: React.ReactNode[] = []

    // Title cell
    rowCells.push(
      <td key="title" css={classes.fixedCell} colSpan={config.titleColSpan}>
        {config.title}
      </td>
    )

    // Data cells
    config.columnKeys.forEach((columnKey, colIndex) => {
      const cellKey = getCellKey(config.key, columnKey)
      const cellValue = tableData[cellKey] || ''

      if (config.cellColSpan && colIndex === 0) {
        // Single cell spanning multiple columns
        rowCells.push(
          <td key={cellKey} colSpan={config.cellColSpan}>
            <div css={classes.nativeInputWrapper}>
              <input
                type="text"
                value={cellValue}
                onChange={(e) => updateCell(cellKey, e.target.value)}
                css={classes.nativeInput}
              />
              <span css={classes.nativeInputSuffix}>°</span>
            </div>
          </td>
        )
      } else if (!config.cellColSpan) {
        // Multiple cells per row
        rowCells.push(
          <td key={cellKey}>
            <div css={classes.nativeInputWrapper}>
              <input
                type="text"
                value={cellValue}
                onChange={(e) => updateCell(cellKey, e.target.value)}
                css={classes.nativeInput}
              />
              <span css={classes.nativeInputSuffix}>°</span>
            </div>
          </td>
        )
      }
    })

    return <tr key={config.rowIndex}>{rowCells}</tr>
  }

  return (
    <div css={classes.tableContainer(MIN_TABLE_WIDTH)}>
      <table css={classes.table}>
        <thead>
          <tr>
            <th>
              <input
                type="text"
                value={tableData[PATIENT_AGE_SEX_KEY] || ''}
                onChange={(e) => updateCell(PATIENT_AGE_SEX_KEY, e.target.value)}
                placeholder="Enter patient's age and sex"
                css={classes.nativeInput}
              />
            </th>
            <th css={classes.fixedCell}>Mean</th>
            <th css={classes.fixedCell}>Patient</th>
          </tr>
        </thead>
        <tbody>{ORTHODONTIC_TABLE_ROWS.map((config) => renderRow(config))}</tbody>
      </table>
    </div>
  )
}
